import { Injectable, inject, NgZone } from '@angular/core';
import { Auth, signInWithEmailAndPassword, signOut, User, getIdToken, UserCredential, onAuthStateChanged, connectAuthEmulator } from '@angular/fire/auth';
import { setPersistence, browserLocalPersistence } from 'firebase/auth';
import { Observable, from, map, of, BehaviorSubject } from 'rxjs';
import { switchMap, tap, take, catchError, filter } from 'rxjs/operators';
import { UserStateService } from '../../../core/services/user-state.service';
import { Router } from '@angular/router';
import { Firestore, doc, getDoc, connectFirestoreEmulator } from '@angular/fire/firestore';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private auth: Auth = inject(Auth);
  private firestore: Firestore = inject(Firestore);
  private userState = inject(UserStateService);
  private router = inject(Router);
  private ngZone = inject(NgZone);
  private showDebugInfo = environment.showDebugInfo;
  
  // Track if auth is ready (initialized)
  private authReadySubject = new BehaviorSubject<boolean>(false);
  readonly authReady$ = this.authReadySubject.asObservable();
  
  // Collection path with environment
  private readonly usersCollection = `environments/${environment.environmentName}/users`;
  
  // Expose the user state observables
  readonly authState$ = this.userState.user$;
  readonly isAdmin$ = this.userState.isAdmin$;
  readonly organisationId$ = this.userState.organisationId$;
  readonly role$ = this.userState.role$;
  readonly isLoading$ = this.userState.loading$;

  constructor() {
    if (this.showDebugInfo) {
      console.log('Auth service initialized');
    }
    
    // Connect to emulators in development environment
    if (!environment.production && environment.useEmulators) {
      try {
        if (this.showDebugInfo) {
          console.log('Connecting to Firebase emulators...');
        }
        connectAuthEmulator(this.auth, 'http://localhost:9099', { disableWarnings: true });
        connectFirestoreEmulator(this.firestore, 'localhost', 8080);
        if (this.showDebugInfo) {
          console.log('Connected to Firebase emulators');
        }
      } catch (error) {
        console.error('Error connecting to Firebase emulators:', error);
      }
    }
    
    // Set auth persistence to local on service initialization
    this.setupPersistence();
    
    // Initialize auth state monitoring
    this.initializeAuthState();
  }
  
  /**
   * Initialize auth state monitoring
   * This is called during service construction
   */
  private initializeAuthState(): void {
    // Listen for auth state changes
    onAuthStateChanged(this.auth, async (user) => {
      if (this.showDebugInfo) {
        console.log('Auth service: Auth state changed:', user ? `User: ${user.email}` : 'No user');
      }
      
      if (user) {
        try {
          // Force token refresh to ensure we have the latest claims
          await user.getIdToken(true);
          
          // Update user state
          await this.refreshUserState();
        } catch (error) {
          console.error('Error handling auth state change:', error);
        }
      } else {
        // Clear user state when signed out
        this.userState.clearState();
      }
      
      // Mark auth as ready
      if (!this.authReadySubject.value) {
        this.authReadySubject.next(true);
      }
    }, (error) => {
      console.error('Error in auth state change listener:', error);
      // Mark auth as ready even on error to avoid blocking the app
      if (!this.authReadySubject.value) {
        this.authReadySubject.next(true);
      }
    });
  }
  
  /**
   * Get the current Firebase user
   * @returns The current Firebase user or null if not authenticated
   */
  getCurrentUser(): User | null {
    return this.auth.currentUser;
  }
  
  /**
   * Set Firebase auth persistence to local to maintain auth state across page refreshes
   */
  private async setupPersistence(): Promise<void> {
    try {
      // Check if we already have a current user before trying to set persistence
      // This helps avoid the error when persistence is already set
      const currentUser = this.auth.currentUser;
      if (currentUser) {
        if (this.showDebugInfo) {
          console.log('Current user already available, skipping persistence setup:', currentUser.email);
        }
        return;
      }
      
      await setPersistence(this.auth, browserLocalPersistence);
      if (this.showDebugInfo) {
        console.log('Firebase Auth persistence set to local');
      }
    } catch (error) {
      // Don't let persistence errors block authentication
      console.error('Error setting auth persistence:', error);
      
      // Specific handling for the "cls is not a constructor" error
      // This is a known issue with Firebase Auth in some environments
      if (error instanceof TypeError && error.message.includes('cls is not a constructor')) {
        if (this.showDebugInfo) {
          console.log('Encountered known persistence error, continuing with default persistence');
        }
      }
    }
  }

  /**
   * Sign in with email and password
   */
  async signIn(email: string, password: string): Promise<UserCredential> {
    try {
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      if (this.showDebugInfo) {
        console.log('User signed in successfully:', userCredential.user.email);
      }
      
      // Force refresh user state to load Firestore data
      await this.refreshUserState();
      
      return userCredential;
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  }

  /**
   * Sign out the current user
   */
  async signOut(): Promise<void> {
    try {
      if (this.showDebugInfo) {
        console.log('AuthService: Signing out user');
      }
      
      // Clear user state first
      this.userState.clearState();
      
      // Then sign out from Firebase
      await signOut(this.auth);
      
      if (this.showDebugInfo) {
        console.log('AuthService: User signed out successfully');
      }
      
      // Navigate to login page
      this.router.navigate(['/login']);
      
      return Promise.resolve();
    } catch (error) {
      console.error('AuthService: Error signing out user', error);
      return Promise.reject(error);
    }
  }

  /**
   * Gets the user's custom claims (roles) from Firebase Auth
   * @param user The Firebase user object
   * @returns A promise that resolves to an object containing the user's roles
   */
  async getUserRoles(user: User): Promise<any> {
    try {
      // Get the ID token with force refresh to ensure we have the latest claims
      const idTokenResult = await user.getIdTokenResult(true);
      
      // Log the full token result for debugging
      if (this.showDebugInfo) {
        console.log('ID Token claims:', idTokenResult.claims);
        console.log('Admin claim:', idTokenResult.claims['admin']);
      }
      
      // Get additional user data from Firestore
      try {
        const userDocRef = doc(this.firestore, `${this.usersCollection}/${user.uid}`);
        const userDoc = await getDoc(userDocRef);
        
        let userData = {};
        
        if (userDoc.exists()) {
          userData = userDoc.data();
          if (this.showDebugInfo) {
            console.log('User data from Firestore:', userData);
          }
        } else {
          if (this.showDebugInfo) {
            console.log('No user document found in Firestore');
          }
        }
        
        // Return combined data from claims and Firestore
        return {
          ...idTokenResult.claims,
          ...userData
        };
      } catch (firestoreError) {
        console.error('Error accessing Firestore data:', firestoreError);
        // If Firestore fails, still return the claims from the token
        if (this.showDebugInfo) {
          console.log('Falling back to token claims only');
        }
        return idTokenResult.claims;
      }
    } catch (error) {
      console.error('Error getting user roles:', error);
      // Return minimal data to avoid breaking the app
      return {
        admin: false,
        role: 'user'
      };
    }
  }

  /**
   * Checks if the current user has admin role
   * @returns An observable that resolves to true if the user is an admin, false otherwise
   */
  isAdmin(): Observable<boolean> {
    return this.userState.isAdmin$.pipe(
      tap(isAdmin => {
        if (this.showDebugInfo) {
          console.log('isAdmin() check result:', isAdmin);
        }
      })
    );
  }

  /**
   * Checks if the current user has a specific role
   * @param role The role to check for
   * @returns An observable that resolves to true if the user has the role, false otherwise
   */
  hasRole(role: string): Observable<boolean> {
    return this.userState.state$.pipe(
      take(1),
      map(state => {
        // If user is admin, they have all roles
        if (state.isAdmin) {
          if (this.showDebugInfo) {
            console.log(`User is admin, granting access to role: ${role}`);
          }
          return true;
        }
        
        // Check if the user's role matches the requested role
        const hasRole = state.role === role;
        if (this.showDebugInfo) {
          console.log(`Checking for role ${role}, user has role: ${state.role}, match: ${hasRole}`);
        }
        return hasRole;
      })
    );
  }

  /**
   * Force refresh the user state
   * @returns A promise that resolves when the user state has been refreshed
   */
  refreshUserState(): Promise<void> {
    return this.userState.refreshUserState();
  }
  
  /**
   * Check if the user is authenticated and if not, try to refresh the state
   * @returns An observable that resolves to true if authenticated, false otherwise
   */
  checkAuthenticated(): Observable<boolean> {
    return this.authState$.pipe(
      take(1),
      switchMap(user => {
        if (user) {
          if (this.showDebugInfo) {
            console.log('User is authenticated:', user.email);
          }
          return of(true);
        }
        
        if (this.showDebugInfo) {
          console.log('No user in authState$, trying to refresh state...');
        }
        
        // Check if there's a current user in Firebase Auth
        const currentUser = this.auth.currentUser;
        if (currentUser) {
          if (this.showDebugInfo) {
            console.log('Found current user in Firebase Auth:', currentUser.email);
          }
          return from(this.refreshUserState()).pipe(
            map(() => true),
            catchError(error => {
              console.error('Error refreshing state from current user:', error);
              return of(false);
            })
          );
        }
        
        return from(this.refreshUserState()).pipe(
          switchMap(() => this.authState$.pipe(take(1))),
          map(refreshedUser => {
            const isAuthenticated = !!refreshedUser;
            if (this.showDebugInfo) {
              console.log('After refresh, user is authenticated:', isAuthenticated);
            }
            return isAuthenticated;
          }),
          catchError(error => {
            console.error('Error refreshing auth state:', error);
            return of(false);
          })
        );
      })
    );
  }
  
  /**
   * Wait for auth to be ready
   * @returns A promise that resolves when auth is ready
   */
  waitForAuthReady(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      // If auth is already ready, resolve immediately
      if (this.authReadySubject.value) {
        resolve(true);
        return;
      }
      
      // Otherwise, wait for auth to be ready
      const subscription = this.authReady$.pipe(
        filter(ready => ready),
        take(1)
      ).subscribe({
        next: () => {
          resolve(true);
          subscription.unsubscribe();
        },
        error: (error) => {
          console.error('Error waiting for auth ready:', error);
          resolve(false);
          subscription.unsubscribe();
        }
      });
      
      // Set a timeout to avoid hanging indefinitely
      setTimeout(() => {
        if (!this.authReadySubject.value) {
          if (this.showDebugInfo) {
            console.log('Auth ready timeout reached');
          }
          this.authReadySubject.next(true);
        }
      }, 3000);
    });
  }

  /**
   * Get the Firebase Auth instance
   * @returns The Firebase Auth instance
   */
  getAuth(): Auth {
    return this.auth;
  }
} 